import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';

// Register user from website with complete profile data
export const registerUserFromWebsite = async (userData) => {
  try {
    // Step 1: Create Firebase Auth user
    const { email, password, ...profileData } = userData;
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Step 2: Update user profile with display name
    await updateProfile(user, {
      displayName: `${profileData.firstName} ${profileData.lastName}`,
    });
    
    // Step 3: Create user document in Firestore with complete structure
    const userDocRef = doc(db, 'users', user.uid);
    const userDataToSave = {
      // Basic Info
      id: user.uid,
      email: user.email,
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      gender: profileData.gender,
      phoneNumber: profileData.phoneNumber,
      
      // Additional Info
      day: profileData.day,
      month: profileData.month,
      year: profileData.year,
      addressLine1: profileData.addressLine1,
      addressLine2: profileData.addressLine2 || '',
      addressLine3: profileData.addressLine3 || '',
      
      // Prayer Unit
      prayerUnit: profileData.prayerUnit,
      prayerUnitName: profileData.prayerUnitName,
      
      // System Fields
      registrationDate: new Date().toISOString(),
      createdAt: new Date(),
      updatedAt: new Date(),
      preregisteredFromWebsite: true,
      websiteRegistrationDate: new Date(),
      
      // Initialize prayer tracking data (same as mobile app)
      prayerStats: {
        totalSessions: 0,
        totalTime: 0,
        averageTime: 0,
        todaySessions: 0,
        todayTime: 0,
        weeklySessions: 0,
        weeklyTime: 0,
        prayerTypes: {
          prayerPoint: 0,
          prayerGoal: 0,
          prayerFast: 0,
          personal: 0
        }
      },
      prayerSessions: [],
      currentPrayerFast: null,
      prayerFastHistory: [],
      prayerGoals: [],
      answeredPrayers: [],
      prayerReminders: []
    };
    
    await setDoc(userDocRef, userDataToSave);
    
    return {
      success: true,
      userId: user.uid,
      user: userDataToSave
    };
    
  } catch (error) {
    console.error('Website registration error:', error);
    throw error;
  }
};

// Sign in user
export const signInUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get user data from Firestore
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    
    return {
      success: true,
      user: user,
      userData: userDoc.exists() ? userDoc.data() : null
    };
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
};

// Sign out user
export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

// Get current user data
export const getCurrentUserData = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error('Get user data error:', error);
    throw error;
  }
};
